import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchBrandManager, fetchBrandManagers, } from '@/entities/brand-manager';
import { assignBmAction } from '@/features/assign-bm';
import { ApiError } from '@/shared/api';
import { agentCountLabel, fixationStatusLabel, uiCopy } from '@/shared/copy';
import { Alert, AlertDescription, AlertTitle, Badge, Button, DirectoryPanel, SearchField, Sheet, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from '@/shared/ui';
import { FreeAgentPicker } from '@/widgets/free-agent-picker';
function normalize(value) {
    return value.trim().toLocaleLowerCase('ru-RU').replaceAll('ё', 'е');
}
function pluralManagers(count) {
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 === 1 && mod100 !== 11)
        return 'бренд-менеджер';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
        return 'бренд-менеджера';
    }
    return 'бренд-менеджеров';
}
function fixationTone(status) {
    if (status === 'ACTIVE')
        return 'success';
    if (status === 'ENDING')
        return 'warning';
    return 'neutral';
}
export function BrandManagerList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const query = searchParams.get('q') ?? '';
    const [draftQ, setDraftQ] = useState(query);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null);
    const [assignOpen, setAssignOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchBrandManagers();
            setItems(data.items);
        }
        catch (err) {
            setError(err instanceof ApiError ? err.message : uiCopy.loadFailed);
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        void load();
    }, [load]);
    useEffect(() => {
        setDraftQ(query);
    }, [query]);
    const writeQuery = useCallback((next) => {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            if (!next)
                params.delete('q');
            else
                params.set('q', next);
            return params;
        }, { replace: true });
    }, [setSearchParams]);
    useEffect(() => {
        if (draftQ.trim() === query)
            return;
        const timer = window.setTimeout(() => writeQuery(draftQ.trim()), 250);
        return () => window.clearTimeout(timer);
    }, [draftQ, query, writeQuery]);
    function resetQuery() {
        setDraftQ('');
        writeQuery('');
    }
    async function openManager(managerId) {
        setError(null);
        try {
            setSelected(await fetchBrandManager(managerId));
            setAssignOpen(false);
        }
        catch (err) {
            setError(err instanceof ApiError ? err.message : uiCopy.loadFailed);
        }
    }
    const rows = useMemo(() => {
        const needle = normalize(query);
        if (!needle)
            return items;
        return items.filter((item) => normalize(item.displayName).includes(needle));
    }, [items, query]);
    const showSkeleton = loading && items.length === 0;
    return (_jsxs("div", { children: [_jsxs("div", { className: "space-y-3", children: [error ? (_jsxs(Alert, { variant: "destructive", children: [_jsx(AlertTitle, { children: uiCopy.errorTitle }), _jsx(AlertDescription, { children: error }), _jsx(Button, { type: "button", className: "mt-3", variant: "outline", onClick: () => void load(), children: uiCopy.retry })] })) : null, _jsx(DirectoryPanel, { total: _jsxs(_Fragment, { children: [_jsx("strong", { children: rows.length }), " ", pluralManagers(rows.length)] }), actions: query ? (_jsx("button", { type: "button", className: "text-[11px] text-primary hover:underline", onClick: resetQuery, children: "\u0421\u0431\u0440\u043E\u0441\u0438\u0442\u044C" })) : null, search: _jsx(SearchField, { className: "w-full", value: draftQ, onValueChange: setDraftQ, placeholder: uiCopy.brandManagerSearch, "aria-label": "\u041F\u043E\u0438\u0441\u043A \u0431\u0440\u0435\u043D\u0434-\u043C\u0435\u043D\u0435\u0434\u0436\u0435\u0440\u043E\u0432" }), footer: "\u0421\u0442\u0440\u043E\u043A\u0430 \u043E\u0442\u043A\u0440\u044B\u0432\u0430\u0435\u0442 \u0430\u0433\u0435\u043D\u0442\u043E\u0432 \u044D\u0442\u043E\u0433\u043E \u0431\u0440\u0435\u043D\u0434-\u043C\u0435\u043D\u0435\u0434\u0436\u0435\u0440\u0430.", children: showSkeleton ? (_jsx(Skeleton, { className: "h-64 w-full rounded-none" })) : error && items.length === 0 ? null : !error && items.length === 0 ? (_jsx("div", { className: "px-4 py-6", children: _jsxs(Alert, { children: [_jsx(AlertTitle, { children: uiCopy.brandManagersEmptyTitle }), _jsx(AlertDescription, { children: uiCopy.brandManagersEmptyHint })] }) })) : rows.length === 0 ? (_jsx("p", { className: "px-4 py-6 text-sm text-muted-foreground", children: "\u0421\u0442\u0440\u043E\u043A \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u043E." })) : (_jsxs(Table, { density: "compact", framed: false, "aria-label": "\u0411\u0440\u0435\u043D\u0434-\u043C\u0435\u043D\u0435\u0434\u0436\u0435\u0440\u044B", children: [_jsx(TableHeader, { children: _jsxs(TableRow, { children: [_jsx(TableHead, { children: "\u0411\u0440\u0435\u043D\u0434-\u043C\u0435\u043D\u0435\u0434\u0436\u0435\u0440" }), _jsx(TableHead, { "data-align": "right", children: "\u0410\u0433\u0435\u043D\u0442\u044B" })] }) }), _jsx(TableBody, { children: rows.map((manager) => (_jsxs(TableRow, { "data-clickable": "true", tabIndex: 0, onClick: () => void openManager(manager.managerId), onKeyDown: (event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                void openManager(manager.managerId);
                                            }
                                        }, children: [_jsxs(TableCell, { children: [_jsx("span", { className: "font-medium text-primary", children: manager.displayName }), manager.isCurrent ? (_jsx(Badge, { tone: "info", className: "ml-2", children: uiCopy.currentManagerBadge })) : null] }), _jsx(TableCell, { "data-align": "right", children: manager.agentCount })] }, manager.managerId))) })] })) })] }), _jsx(Sheet, { open: selected !== null, onClose: () => {
                    setSelected(null);
                    setAssignOpen(false);
                }, title: selected?.displayName ?? 'Бренд-менеджер', children: selected ? (_jsxs("div", { className: "space-y-4 text-sm", children: [_jsxs("p", { children: [agentCountLabel(selected.agentCount), selected.isCurrent ? (_jsxs(_Fragment, { children: [' ', _jsx(Badge, { tone: "info", children: uiCopy.currentManagerBadge })] })) : null] }), assignOpen ? (_jsx(FreeAgentPicker, { busy: busy, excludeAgentIds: selected.agents.map((agent) => agent.agentId), onPick: (agent) => {
                                void (async () => {
                                    setBusy(true);
                                    try {
                                        await assignBmAction(agent.agentId, selected.managerId);
                                        await openManager(selected.managerId);
                                        await load();
                                        setAssignOpen(false);
                                    }
                                    catch (err) {
                                        setError(err instanceof ApiError ? err.message : uiCopy.actionFailed);
                                    }
                                    finally {
                                        setBusy(false);
                                    }
                                })();
                            } })) : (_jsxs(_Fragment, { children: [selected.agents.length === 0 ? (_jsx("p", { className: "text-muted-foreground", children: "\u0417\u0430 \u044D\u0442\u0438\u043C \u0431\u0440\u0435\u043D\u0434-\u043C\u0435\u043D\u0435\u0434\u0436\u0435\u0440\u043E\u043C \u043F\u043E\u043A\u0430 \u043D\u0435\u0442 \u0437\u0430\u043A\u0440\u0435\u043F\u043B\u0451\u043D\u043D\u044B\u0445 \u0430\u0433\u0435\u043D\u0442\u043E\u0432." })) : (_jsxs(Table, { density: "compact", "aria-label": "\u0410\u0433\u0435\u043D\u0442\u044B \u0431\u0440\u0435\u043D\u0434-\u043C\u0435\u043D\u0435\u0434\u0436\u0435\u0440\u0430", children: [_jsx(TableHeader, { children: _jsxs(TableRow, { children: [_jsx(TableHead, { children: "\u0410\u0433\u0435\u043D\u0442" }), _jsx(TableHead, { children: "\u0424\u0438\u043A\u0441\u0430\u0446\u0438\u044F" })] }) }), _jsx(TableBody, { children: selected.agents.map((agent) => (_jsxs(TableRow, { children: [_jsx(TableCell, { children: _jsx(Link, { className: "font-medium text-primary hover:underline", to: `/agents/${agent.agentId}`, children: agent.displayName }) }), _jsx(TableCell, { children: _jsx(Badge, { tone: fixationTone(agent.fixationStatus), children: fixationStatusLabel(agent.fixationStatus) }) })] }, agent.agentId))) })] })), _jsxs(Button, { type: "button", onClick: () => setAssignOpen(true), children: [uiCopy.assignAction, " \u0430\u0433\u0435\u043D\u0442\u0430"] })] }))] })) : null })] }));
}
